import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import { api, idr } from "../../../lib/api";

// Impor berkas hasil klaim dari SUPPLIER (brief owner 22 Sep 2026 A10, PP4).
// Backend (`POST /cashback/claims/{id}/impor-hasil-supplier`) hanya MEMBACA
// berkas dan mengembalikan USULAN — tidak ada yang tersimpan. Usulan mengisi
// pilihan di modal Putuskan; staf meninjau, lalu "Simpan N Keputusan" memakai
// `POST /approve` yang sama. IMEI yang tidak ada di berkas dibiarkan belum
// diputuskan (berkas supplier berisi yang BERHASIL; ketiadaan bukan penolakan).
// Berkas ber-kolom Status: baris DITOLAK dipilihkan "Tolak" (`ditolak_di_berkas`),
// status yang tidak dikenali jatuh ke "Perlu ditinjau" — tidak ditebak.

// SETIAP daftar di respons backend punya satu baris di sini — dijaga tes layar
// PP4 (daftar diambil dari keluaran `pratinjau` sungguhan).
export const DAFTAR_HASIL_SUPPLIER = [
  { kunci: "usulan", judul: "Diusulkan DISETUJUI (sudah dicentang di bawah — periksa sebelum simpan)", nada: "emerald" },
  // Kolom Status berkas bernilai DITOLAK (tinjauan PP r2): dulu baris ini ikut
  // diusulkan disetujui. Kini dipilihkan "Tolak" dengan alasan dari berkas.
  { kunci: "ditolak_di_berkas", judul: "DITOLAK supplier menurut kolom Status (sudah dipilih Tolak — periksa sebelum simpan)", nada: "rose" },
  { kunci: "sudah_diputuskan", judul: "Sudah diputuskan sebelumnya — dilewati", nada: "slate" },
  { kunci: "konflik", judul: "Konflik IMEI / SN — putuskan manual", nada: "rose" },
  { kunci: "perlu_review", judul: "Perlu ditinjau — tidak diisi otomatis", nada: "amber" },
  { kunci: "ganda_di_berkas", judul: "Muncul lebih dari sekali di berkas", nada: "amber" },
  { kunci: "tidak_cocok", judul: "Tidak cocok dengan batch ini", nada: "rose" },
  { kunci: "tidak_terbaca", judul: "Sel IMEI/SN tidak terbaca", nada: "rose" },
  { kunci: "tidak_ada_di_berkas", judul: "IMEI batch yang TIDAK ada di berkas — dibiarkan belum diputuskan", nada: "slate" },
];

const NADA = {
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
  slate: "border-slate-200 bg-slate-50 text-slate-700",
  rose: "border-rose-200 bg-rose-50 text-rose-900",
  amber: "border-amber-200 bg-amber-50 text-amber-900",
};

// "identitas" = unit ditemukan lewat data inventori (mis. SN tertulis di kolom
// "IMEI"), lalu dicocokkan ke item batch ini lewat unitnya (tinjauan PP r2).
const LABEL_COCOK = { imei: "IMEI", sn: "SN", identitas: "data unit (IMEI/SN)" };

function teksBaris(kunci, b) {
  const id = b.imei || b.imei_berkas || b.sn_berkas || "-";
  const awal = b.baris ? `Baris ${b.baris} · ` : "";
  if (kunci === "usulan") {
    return `${awal}${b.imei} · disetujui ${idr(b.approved_nominal)} dari klaim ${idr(b.cashback_nominal)}`
      + ` (cocok lewat ${LABEL_COCOK[b.cocok_lewat] || "IMEI"})${b.catatan ? ` — ${b.catatan}` : ""}`;
  }
  if (kunci === "ditolak_di_berkas") return `${awal}${b.imei} · status berkas "${b.status_berkas}" — ${b.alasan_tolak}`;
  if (kunci === "sudah_diputuskan") return `${awal}${id} · status ${b.status}`;
  return `${awal}${id}${b.alasan ? ` — ${b.alasan}` : ""}`;
}

export function HasilImporSupplier({ hasil }) {
  if (!hasil) return null;
  return (
    <div className="space-y-2" data-testid="cb-approve-impor-hasil">
      <p className="text-xs text-slate-600" data-testid="cb-approve-impor-ringkas">
        {hasil.baris_dibaca} baris terbaca dari sheet {hasil.sheet || "-"} · {(hasil.usulan || []).length} diusulkan disetujui
        {(hasil.ditolak_di_berkas || []).length > 0 && ` · ${hasil.ditolak_di_berkas.length} ditolak supplier`}.
        {(hasil.judul || {}).status && ` Keputusan per baris dibaca dari kolom “${hasil.judul.status}”.`}
        {(hasil.judul_tidak_dikenal || []).length > 0 && ` Kolom diabaikan: ${hasil.judul_tidak_dikenal.join(", ")}.`}
      </p>
      {/* Kolom nominal yang BENAR-BENAR dibaca (tinjauan PP r1): dulu berkas ber-
          "Nominal Klaim" + "Nominal Disetujui" diam-diam memakai kolom pertama dan
          kolom kedua hanya disebut "diabaikan". Backend kini membaca kolom
          "Disetujui"; kolom nominal lain disebut terpisah di sini. */}
      {(hasil.judul || {}).nominal && (
        <p className="text-xs text-slate-600" data-testid="cb-approve-impor-kolom-nominal">
          Nominal disetujui dibaca dari kolom “{hasil.judul.nominal}”.
          {(hasil.judul_nominal_lain || []).length > 0
            && ` Kolom nominal lain TIDAK dipakai: ${hasil.judul_nominal_lain.join(", ")}.`}
        </p>
      )}
      {DAFTAR_HASIL_SUPPLIER.filter((d) => (hasil[d.kunci] || []).length > 0).map((d) => (
        <details key={d.kunci} open={d.kunci !== "tidak_ada_di_berkas"} data-testid={`cb-approve-impor-daftar-${d.kunci}`}
                 className={`border rounded-lg p-2 text-xs ${NADA[d.nada]}`}>
          <summary className="font-semibold cursor-pointer">{d.judul} ({hasil[d.kunci].length})</summary>
          <ul className="mt-1 space-y-0.5">
            {hasil[d.kunci].map((b, i) => (
              <li key={i} data-testid={`cb-approve-impor-baris-${d.kunci}`}>{teksBaris(d.kunci, b)}</li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

export default function ImporHasilSupplier({ claimId, cbBase, onUsulan }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [hasil, setHasil] = useState(null);
  const pilih = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const r = await api.post(`${cbBase}/claims/${claimId}/impor-hasil-supplier`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setHasil(r.data);
      onUsulan?.(r.data);
      const ditolak = (r.data.ditolak_di_berkas || []).length;
      toast.success(`${(r.data.usulan || []).length} IMEI diusulkan disetujui`
        + (ditolak ? `, ${ditolak} ditolak supplier` : "") + " — periksa lalu simpan");
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal membaca berkas hasil supplier"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-slate-500">Punya berkas hasil klaim dari supplier? Impor untuk mencocokkan IMEI/SN otomatis.</p>
        <button type="button" onClick={() => input.current && input.current.click()} disabled={busy}
                data-testid="cb-approve-impor-supplier"
                className="h-8 px-3 rounded-lg border border-emerald-200 text-emerald-700 text-xs font-medium inline-flex items-center gap-1.5 hover:bg-emerald-50 disabled:opacity-50">
          <FileSpreadsheet className="w-3.5 h-3.5" /> {busy ? "Membaca..." : "Impor hasil supplier (.xlsx)"}
        </button>
        <input ref={input} type="file" accept=".xlsx" className="hidden" onChange={pilih} data-testid="cb-approve-impor-file" />
      </div>
      <HasilImporSupplier hasil={hasil} />
    </div>
  );
}
